import React, { Fragment, useEffect, useState } from "react";
import Image from "next/image";
import { PostItem } from "@/components/modules/Post/PostItem";
import { AuthorPageContentProps } from "@/types/modules/post/components";
import styles from "@/styles/app.module.css";
import { usePostsByAuthorPagingInfo } from "@/core/dataProviders/providers/paging/getPagingInfoHooks";
import usePostsByAuthorSlugProvider from "@/core/dataProviders/providers/posts/usePostsByAuthorSlugProvider";
import { SocialButtons } from "@/components/ui-kit/Buttons/SocialButtons";
import useSettingsProvider from "@/core/dataProviders/providers/app/useSettingsProvider";
import Head from "next/head";
import { Pagination } from "@/components/ui-kit/Pagination";
import { perPage } from "@/utils/getPostPerPage";
import { getUniquePosts } from "@/utils/getUniquePosts";
import { AdvertisementBlock } from "@/components/layout/AdvertisementBlock";
import useTrackPageView from "@/hooks/data-tracking/useTrackPageView";

export const AuthorPageContent: React.FC<AuthorPageContentProps> = ({ author }) => {
  const [settings] = useSettingsProvider();
  const pageType = author?.name ?? "author";
  useTrackPageView(pageType, author);
  const [pageNumber, setPageNumber] = useState(1);

  const [postsByAuthorSlug, postsByAuthorSlugReqState] = usePostsByAuthorSlugProvider(
    author?.slug && perPage
      ? {
          slug: author?.slug,
          pageNumber: pageNumber,
          perPage: perPage,
        }
      : null
  );

  const [allPostsByAuthorSlug, setAllPostsByAuthorSlug] = useState(postsByAuthorSlug);

  const pagingInfo = usePostsByAuthorPagingInfo(
    author?.slug && perPage
      ? {
          slug: author.slug,
          perPage: perPage,
        }
      : null
  );

  useEffect(() => {
    if (author?.id) {
      setAllPostsByAuthorSlug([]);
      setPageNumber(1);
    }
  }, [author?.id]);

  useEffect(() => {
    setAllPostsByAuthorSlug((prev) => getUniquePosts(prev, postsByAuthorSlug));
  }, [postsByAuthorSlug]);

  return (
    <>
      <Head>
        <title>{author?.yoastHeadJson?.title ?? `${author?.name} - Magazín Reportér`}</title>
        <meta
          property="og:title"
          content={author?.yoastHeadJson?.og_title ?? `${author?.name} - Magazín Reportér`}
          key="og:title"
        />
        {(author?.yoastHeadJson?.description || author?.shortDescription) && (
          <meta
            name="description"
            content={author?.yoastHeadJson?.description ?? author?.shortDescription}
            key="description"
          />
        )}
        {(author?.yoastHeadJson?.og_description || author?.shortDescription) && (
          <meta
            property="og:description"
            content={author?.yoastHeadJson?.og_description ?? author?.shortDescription}
            key="og:description"
          />
        )}
        {author?.image?.url && <meta property="og:image" content={author?.image?.url} key="og:image" />}
        {author?.yoastHeadJson?.og_type && (
          <meta property="og:type" content={author?.yoastHeadJson?.og_type ?? "author"} key="og:type" />
        )}
        {(author?.yoastHeadJson?.og_url || author?.link) && (
          <meta property="og:url" content={author?.yoastHeadJson?.og_url ?? author?.link} key="og:url" />
        )}
        {author?.yoastHeadJson?.schema && (
          <script
            key="head-json-ld"
            id="meta-schema"
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(author.yoastHeadJson.schema) }}
          ></script>
        )}
      </Head>

      <div className="mb-6">
        <div className={styles.contentContainer}>
          <div className="pb-10 pt-10 md:pt-14">
            <div className="flex flex-col items-start justify-center md:items-center lg:mb-12 lg:flex-row">
              <div className="flex flex-col md:text-center lg:text-left">
                <span className="mb-2 font-mercury text-6xl font-semibold dark:text-white">{author?.name}</span>
                {author?.jobTitle && (
                  <span className="mb-5 font-sans text-md font-light dark:text-white-70 md:mb-7">
                    {author?.jobTitle}
                  </span>
                )}
                {author?.shortDescription && (
                  <p className="text-2xl font-semibold dark:text-white">{author?.shortDescription}</p>
                )}
                {(author?.socials?.facebook || author?.socials?.twitter) && (
                  <div className="mt-6 flex items-center md:justify-center lg:justify-start">
                    <SocialButtons author={author!} />
                  </div>
                )}
              </div>
              <Image
                src={author?.image?.url ?? settings?.authorFallbackImage ?? ""}
                width={300}
                height={400}
                className="mx-auto my-[25px] h-[300px] w-[400px] object-contain lg:my-0 lg:ml-10 lg:h-[235px] lg:w-[175px]"
                alt={author?.image?.alt ?? ""}
              />
            </div>
            <div className="lg:max-w-[375px]">
              <p className="text-base dark:text-white lg:mt-12">{author?.description}</p>
            </div>
          </div>
          {!!allPostsByAuthorSlug.length && (
            <>
              {allPostsByAuthorSlug?.map((item, key) => (
                <Fragment key={`post-item-${key}`}>
                  <PostItem post={item} />
                  {key === 2 && <AdvertisementBlock id={"protag-Leaderboard_in_feed_2"} />}
                </Fragment>
              ))}
              {pagingInfo && pagingInfo?.pages > pageNumber && (
                <Pagination loadMore={() => setPageNumber(pageNumber + 1)} requestState={postsByAuthorSlugReqState} />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
